/**
 * Architecture Drawings Library
 * 
 * Functions for fetching and working with architectural floor plan drawings.
 * These drawings are mapped to unit_type codes and contain optimized responsive images.
 */

import { supabase } from '@/integrations/supabase/client';
import type { PropertyUnit } from './units';

/**
 * Image URLs structure from the architecture_drawings table
 */
export interface ArchitectureImageUrls {
  '640_avif'?: string;
  '640_webp'?: string;
  '640_jpeg'?: string;
  '1280_avif'?: string;
  '1280_webp'?: string;
  '1280_jpeg'?: string;
  '1920_avif'?: string;
  '1920_webp'?: string;
  '1920_jpeg'?: string;
  '2560_avif'?: string;
  '2560_webp'?: string;
  '2560_jpeg'?: string;
  '3840_avif'?: string;
  '3840_webp'?: string;
  '3840_jpeg'?: string;
  fallback?: string;
  [key: string]: string | undefined;
}

/**
 * Architecture drawing record from database
 */
export interface ArchitectureDrawing {
  id: string;
  unit_type: string;
  category: '1bed' | '2bed' | 'townhouse';
  image_urls: ArchitectureImageUrls;
  pdf_url: string | null;
  display_name: string;
  applies_to_units: string[];
  created_at: string;
  updated_at: string;
}

/**
 * Processed architecture drawing for component use
 */
export interface ProcessedArchitectureDrawing {
  id: string;
  unitType: string;
  category: string;
  displayName: string;
  imageUrls: ArchitectureImageUrls;
  pdfUrl: string | null;
  thumbnailUrl: string;
  appliesToUnits: string[];
}

/**
 * Get architecture drawing by unit type
 * 
 * @param unitType - The unit type code (e.g., "A1", "B2", "C1")
 * @returns The architecture drawing record or null if not found
 */
export async function getArchitectureDrawing(
  unitType: string
): Promise<ProcessedArchitectureDrawing | null> {
  try {
    const { data, error } = await supabase
      .from('architecture_drawings')
      .select('*')
      .eq('unit_type', unitType.toUpperCase())
      .single();
    
    if (error || !data) {
      // Not found is expected for some unit types
      if (error?.code !== 'PGRST116') {
        console.error('Error fetching architecture drawing:', error);
      }
      return null;
    }
    
    const drawing = data as ArchitectureDrawing;
    const imageUrls = drawing.image_urls || {};
    return {
      id: drawing.id,
      unitType: drawing.unit_type,
      category: drawing.category,
      displayName: drawing.display_name,
      imageUrls,
      pdfUrl: drawing.pdf_url,
      thumbnailUrl: getThumbnailUrl(imageUrls),
      appliesToUnits: drawing.applies_to_units || [],
    };
  } catch (error) {
    console.error('Error fetching architecture drawing:', error);
    return null;
  }
}

/**
 * Get architecture drawing by id
 * Used when a unit has a manual link (architecture_drawing_id).
 */
export async function getArchitectureDrawingById(
  id: string
): Promise<ProcessedArchitectureDrawing | null> {
  try {
    const { data, error } = await supabase
      .from('architecture_drawings')
      .select('*')
      .eq('id', id)
      .single();

    if (error || !data) {
      if (error?.code !== 'PGRST116') {
        console.error('Error fetching architecture drawing by id:', error);
      }
      return null;
    }

    const drawing = data as ArchitectureDrawing;
    const imageUrls = drawing.image_urls || {};
    return {
      id: drawing.id,
      unitType: drawing.unit_type,
      category: drawing.category,
      displayName: drawing.display_name,
      imageUrls,
      pdfUrl: drawing.pdf_url,
      thumbnailUrl: getThumbnailUrl(imageUrls),
      appliesToUnits: drawing.applies_to_units || [],
    };
  } catch (error) {
    console.error('Error fetching architecture drawing by id:', error);
    return null;
  }
}

/**
 * Get architecture drawing for a unit
 * Uses manual link (architecture_drawing_id) when set, else resolves by unit_type.
 *
 * @param unit - The property unit object (or minimal { unit_type, architecture_drawing_id? })
 * @returns The architecture drawing record or null if not found
 */
export async function getArchitectureDrawingForUnit(
  unit: PropertyUnit | { unit_type: string; architecture_drawing_id?: string | null }
): Promise<ProcessedArchitectureDrawing | null> {
  if (unit.architecture_drawing_id) {
    const byId = await getArchitectureDrawingById(unit.architecture_drawing_id);
    if (byId) return byId;
  }
  if (!unit.unit_type) {
    return null;
  }
  return getArchitectureDrawing(unit.unit_type);
}

/**
 * Get all architecture drawings
 * Useful for admin views or preloading
 */
export async function getAllArchitectureDrawings(): Promise<ProcessedArchitectureDrawing[]> {
  try {
    const { data, error } = await supabase
      .from('architecture_drawings')
      .select('*')
      .order('unit_type');
    
    if (error) {
      console.error('Error fetching architecture drawings:', error);
      return [];
    }
    
    return (data || []).map((drawing: ArchitectureDrawing) => {
      const imageUrls = drawing.image_urls || {};
      return {
        id: drawing.id,
        unitType: drawing.unit_type,
        category: drawing.category,
        displayName: drawing.display_name,
        imageUrls,
        pdfUrl: drawing.pdf_url,
        thumbnailUrl: getThumbnailUrl(imageUrls),
        appliesToUnits: drawing.applies_to_units || [],
      };
    });
  } catch (error) {
    console.error('Error fetching architecture drawings:', error);
    return [];
  }
}

/**
 * Get the best thumbnail URL from image URLs
 * Prefers WebP at 640px, falls back to JPEG
 */
export function getThumbnailUrl(imageUrls: ArchitectureImageUrls): string {
  return (
    imageUrls['640_webp'] ||
    imageUrls['640_jpeg'] ||
    imageUrls['640_avif'] ||
    imageUrls.fallback ||
    ''
  );
}

/**
 * Generate srcset string for responsive images
 * 
 * @param imageUrls - The image URLs object
 * @param format - The format to use ('avif', 'webp', or 'jpeg')
 * @returns srcset string for use in img/source elements
 */
export function generateSrcSet(
  imageUrls: ArchitectureImageUrls,
  format: 'avif' | 'webp' | 'jpeg'
): string {
  const widths = [640, 1280, 1920, 2560, 3840];
  const srcSetParts: string[] = [];
  
  for (const width of widths) {
    const key = `${width}_${format}` as keyof ArchitectureImageUrls;
    const url = imageUrls[key];
    if (url) {
      srcSetParts.push(`${url} ${width}w`);
    }
  }
  
  return srcSetParts.join(', ');
}

/**
 * Generate sizes attribute for responsive images
 * Default sizes for floor plan display
 */
export function getDefaultSizes(): string {
  return '(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px';
}

/**
 * Get the fallback image URL
 * Used for browsers that don't support modern formats or as a loading placeholder
 */
export function getFallbackUrl(imageUrls: ArchitectureImageUrls): string {
  return (
    imageUrls.fallback ||
    imageUrls['1920_jpeg'] ||
    imageUrls['1280_jpeg'] ||
    imageUrls['640_jpeg'] ||
    ''
  );
}

/**
 * Check if architecture drawing has valid image URLs
 */
export function hasValidImages(drawing: ProcessedArchitectureDrawing | null): boolean {
  if (!drawing) return false;
  const { imageUrls } = drawing;
  return !!(
    imageUrls.fallback ||
    imageUrls['640_jpeg'] ||
    imageUrls['640_webp'] ||
    imageUrls['640_avif']
  );
}
